import { CalendarDate, getLocalTimeZone, isToday } from '@internationalized/date';
import { clsx } from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { RefObject } from 'react';
import { DateValue, RangeValue, useDateFormatter } from 'react-aria';
import {
  RangeCalendar as AriaRangeCalendar,
  Button,
  CalendarCell,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateRangePickerProps,
  Dialog,
  Heading,
  RangeCalendarProps,
  RangeCalendarState,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import { VStack } from '~/components/layout/Stack';
import Popover from '~/components/popovers/Popover/Popover';
import Text from '~/components/typographies/Text';
import LeftIcon from '~/icons/icon-chevron-left.svg?react';
import RightIcon from '~/icons/icon-chevron-right.svg?react';
import IconFlash from '~/icons/icon-flash.svg?react';
import { dateToCalendarDate } from '~/utils/date';
import { formatDateForDisplay } from '~/utils/formatDate';
import styles from './DateRangePicker.module.scss';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface PresetDate {
  date: Partial<DateRange>;
  id: string;
  label: string;
}

interface CalendarRangePopoverProps {
  defaultValue?: DateRange | null | undefined;
  value?: RangeValue<CalendarDate> | null | undefined;
  defaultFocusedValue?: DateValue | null | undefined;
  isOpen?: boolean | undefined;
  maxRangeDays?: number | undefined;
  onOpenChange?: (isOpen: boolean) => void | undefined;
  onSelectDate?: (date: Partial<DateRange>, preset?: PresetDate) => void | undefined;
  presetDates?: PresetDate[];
  triggerRef?: RefObject<HTMLElement | null> | undefined;
}

const PresetDates = ({
  onSelectDate,
  presetDates,
}: Pick<CalendarRangePopoverProps, 'onSelectDate' | 'presetDates'>) => {
  const { t } = useTranslation('inputs');

  if (!presetDates || isEmpty(presetDates)) return;

  return (
    <VStack gap={2} className={styles.presetDates}>
      <Text variant="body2" weight="semibold" className={styles.presetTitle}>
        {t('date_picker.quick_selections')}
        <IconFlash className={styles.iconFlash} />
      </Text>
      <VStack gap={1} className={styles.presetList}>
        {presetDates.map((presetDate) => (
          <Button
            className={styles.preset}
            key={presetDate.id}
            onPress={() => onSelectDate?.(presetDate.date, presetDate)}
          >
            <Text variant="body2" weight="regular">
              {presetDate.label}
            </Text>
            <Text variant="caption" weight="medium" className={styles.detail}>
              {presetDate.date.start ? formatDateForDisplay(presetDate.date.start) : ''}
              {'-'}
              {presetDate.date.end ? formatDateForDisplay(presetDate.date.end) : ''}
            </Text>
          </Button>
        ))}
      </VStack>
    </VStack>
  );
};

const VISIBLE_MONTHS = 2;

const CalendarRangePopover: React.FunctionComponent<CalendarRangePopoverProps> = ({
  defaultValue,
  value,
  defaultFocusedValue,
  isOpen,
  maxRangeDays,
  onOpenChange,
  onSelectDate,
  presetDates,
  triggerRef,
}) => {
  const { t } = useTranslation('inputs');

  const isDateUnavailable: RangeCalendarProps<CalendarDate>['isDateUnavailable'] = maxRangeDays
    ? (date, anchorDate) => anchorDate != null && Math.abs(date.compare(anchorDate)) >= maxRangeDays
    : undefined;

  const cappedPresetDates = presetDates?.filter(
    ({ date: { start, end } }) =>
      !maxRangeDays || (start && end && differenceInCalendarDays(end, start) < maxRangeDays),
  );

  const onChange: DateRangePickerProps<CalendarDate>['onChange'] = (value): void => {
    if (value && onSelectDate) {
      onSelectDate({
        start: value.start.toDate(getLocalTimeZone()),
        end: value.end.toDate(getLocalTimeZone()),
      });
    }
  };

  return (
    <Popover className={styles.popover} isOpen={isOpen} onOpenChange={onOpenChange} triggerRef={triggerRef}>
      <Dialog aria-label={t('date_picker.calendar')} className={styles.dialog}>
        <PresetDates onSelectDate={onSelectDate} presetDates={cappedPresetDates} />
        <AriaRangeCalendar
          visibleDuration={{ months: VISIBLE_MONTHS }}
          pageBehavior="single"
          className={styles.calendar}
          onChange={onChange}
          isDateUnavailable={isDateUnavailable}
          defaultValue={
            defaultValue
              ? { start: dateToCalendarDate(defaultValue.start), end: dateToCalendarDate(defaultValue.end) }
              : undefined
          }
          value={value}
          defaultFocusedValue={defaultFocusedValue}
        >
          {({ state }) =>
            [...Array(VISIBLE_MONTHS).keys()].map((i) => <CalendarGrid key={i} offset={i} state={state} />)
          }
        </AriaRangeCalendar>
      </Dialog>
    </Popover>
  );
};

const CalendarGrid = ({ offset = 0, state }: { offset?: number; state: RangeCalendarState<DateValue> }) => {
  const localTimeZone = getLocalTimeZone();
  const monthFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
  });
  return (
    <VStack gap={3} className={styles.gridWrapper}>
      <header className={styles.header}>
        {offset === 0 && <IconButton Icon={LeftIcon} size="small" slot="previous" variant="ghost" />}
        <Heading className={styles.heading} level={12}>
          {monthFormatter.format(state.visibleRange.start.add({ months: offset }).toDate(state.timeZone))}
        </Heading>
        {offset === VISIBLE_MONTHS - 1 && <IconButton Icon={RightIcon} size="small" slot="next" variant="ghost" />}
      </header>
      <AriaCalendarGrid className={styles.grid} weekdayStyle="short" offset={{ months: offset }}>
        <CalendarGridHeader>
          {(day) => <CalendarHeaderCell className={styles.headerCell}>{day}</CalendarHeaderCell>}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              className={({ isDisabled }) =>
                clsx(styles.calendarCell, {
                  [styles.disabled!]: isDisabled,
                  [styles.today!]: isToday(date, localTimeZone),
                })
              }
              date={date}
            />
          )}
        </CalendarGridBody>
      </AriaCalendarGrid>
    </VStack>
  );
};

export default CalendarRangePopover;
